import React from "react";
import AppHeading from "../layout/parts/app-heading";
import AppBody from "../layout/parts/app-body";
import Card from "../components/card";
import SimpleInfoBlock from "../components/graphs/simple-info-block";
import LineChart from "../components/graphs/line-chart";
import axios, { responseHandler, errorHandler } from "../requests/axios.js";
import ProgressBarList from "../components/graphs/progress-bar-list.jsx";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ScaleRoundedIcon from "@mui/icons-material/ScaleRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import CasinoRoundedIcon from "@mui/icons-material/CasinoRounded";
import { subDays } from "date-fns";
import { toast } from "react-toastify";
import { useAuth } from "../providers/auth-provider.jsx";
import ChannelSelector from "../components/channel-selector.jsx";
import DateSelector from "../components/date-selector.jsx";
import dateToYmd from "../functions/date-to-ymd.js";

export default function MonetisationPage() {
  const [chartData, setChartData] = React.useState({});
  const [dateRange, setDateRange] = React.useState({
    start: subDays(new Date(), 30),
    end: new Date(),
  });
  const [channels, setChannels] = React.useState([]);
  const [selectedChannels, setSelectedChannels] = React.useState([]);
  const [channelsLoading, setChannelsLoading] = React.useState(false);
  const { user } = useAuth();

  const updateSelectedChannels = React.useCallback((channelOptions) => {
    setSelectedChannels(channelOptions);
  }, []);

  // {start, end}
  const updateDateRange = React.useCallback((rangeObject) => {
    setDateRange(rangeObject);
  }, []);

  React.useEffect(() => {
    setChannelsLoading(true);

    axios
      .get(`api/organisations/${user.organisation.id}/channels`)
      .then(
        responseHandler((response) => {
          setChannels(response.data);
        })
      )
      .catch(errorHandler((e) => {}))
      .finally(() => setChannelsLoading(false));
  }, [user.organisation.id]);

  React.useEffect(() => {
    const controller = new AbortController();

    if (channels.length) {
      const payload = {
        start: dateToYmd(dateRange.start),
        end: dateToYmd(dateRange.end),
      };

      if (selectedChannels.length) {
        payload.channels = selectedChannels
          .map((channel) => `&channels[]=${channel.value}`)
          .join("");
      }

      axios
        .get(
          `api/pages/monetization?start_date=${payload.start}&end_date=${
            payload.end
          }${payload.channels ?? ""}`,
          { signal: controller.signal }
        )
        .then(
          responseHandler((response) => {
            setChartData(response.data);
          })
        )
        .catch(
          errorHandler((e) => {
            if (e.name && e.name === "CanceledError") {
              // do nothing, cancelling old request
            } else {
              console.error(e);
              toast.error("Error loading monetisation data");
            }
          })
        );
    }

    return () => {
      if (channels.length) {
        controller.abort();
      }
    };
  }, [channels.length, dateRange.end, dateRange.start, selectedChannels]);

  return (
    <React.Fragment>
      <AppHeading />

      <AppBody loading={channelsLoading}>
        <div className="monetisation-page">
          <div className="monetisation-page__header-row">
            <ChannelSelector
              channels={channels}
              onChange={updateSelectedChannels}
            />
            <DateSelector updateRange={updateDateRange} />
          </div>
          <div className="monetisation-page__header-row">
            <SimpleInfoBlock
              label="CPM"
              data={chartData.cpm}
              icon={<ScaleRoundedIcon />}
            />
            <SimpleInfoBlock
              label="Revenue"
              data={chartData.revenue}
              icon={<PaidRoundedIcon />}
            />
            <SimpleInfoBlock
              label="Impressions"
              data={chartData.impressions}
              icon={<VisibilityRoundedIcon />}
            />
            <SimpleInfoBlock
              label="Fill Rate"
              data={chartData.fill_rate}
              icon={<SpeedRoundedIcon />}
            />
          </div>
          <div className="monetisation-page__body">
            <div className="monetisation-page__left">
              <Card height={400}>
                <LineChart
                  title="Impressions and Opportunities"
                  subtitle=""
                  datasets={
                    chartData.impressions_and_opportunities?.datasets || []
                  }
                />
              </Card>
              <Card height={400}>
                <LineChart
                  title="Revenue and Cost"
                  subtitle=""
                  datasets={chartData.revenue_and_cost?.datasets || []}
                />
              </Card>
            </div>
            <div className="monetisation-page__right">
              <SimpleInfoBlock
                label="Opportunities"
                data={chartData.opportunities}
                icon={<CasinoRoundedIcon />}
              />
              <Card>
                <ProgressBarList
                  title="Top Channels"
                  data={chartData.top_channels?.datasets}
                />
              </Card>
            </div>
          </div>
        </div>
      </AppBody>
    </React.Fragment>
  );
}
